import { useRef, useMemo, useEffect } from "react";
import { ProgramPhase } from "@core/features/program";
import { useSelectedPhase } from "@platform/store";
import { ProgramPhasesTimelineContainer } from "../../components/PhasesTimeline";
import { ProgramPhaseListContainer } from "../../components/PhaseList";
import { scrollToPhase } from "@platform/features/programPhases";

type Props = {
  currentPhase: ProgramPhase | undefined;
  phases: ProgramPhase[];
  isLoading: boolean;
  programId: number;
};

export function ProgramPhases({
  currentPhase,
  phases,
  isLoading,
  programId,
}: Props) {
  const timelineRef = useRef<HTMLDivElement>(null);

  const phaseContainerRef = useRef<HTMLDivElement>(null);

  const { id: selectedPhaseId, setId: setSelectedPhaseId } = useSelectedPhase();

  const selectedPhase = useMemo(() => {
    if (!selectedPhaseId) return undefined;
    return phases.find((phase) => phase.id === selectedPhaseId);
  }, [selectedPhaseId, phases]);

  const handlePhaseChange = (phase: ProgramPhase) => {
    setSelectedPhaseId(phase.id);

    scrollToPhase({
      index: phases.findIndex((p) => p.id === phase.id),
      phaseContainerRef: phaseContainerRef,
      timelineRef: timelineRef,
      scrollIntoView: true,
    });
  };

  const handlePhaseListReady = () => {
    if (currentPhase) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          handlePhaseChange(currentPhase);
        });
      }, 100);
    }
  };

  // Reset selected phase when component unmounts
  useEffect(() => {
    return () => {
      setSelectedPhaseId(null);
    };
  }, [setSelectedPhaseId]);

  return (
    <>
      <div className="flex flex-col gap-8 py-4 w-full">
        <ProgramPhasesTimelineContainer
          timelineRef={timelineRef}
          phases={phases}
          selectedPhase={selectedPhase}
          setSelectedPhase={handlePhaseChange}
        />
      </div>

      <ProgramPhaseListContainer
        selectedPhase={selectedPhase}
        setSelectedPhase={handlePhaseChange}
        phaseContainerRef={phaseContainerRef}
        onPhaseListReady={handlePhaseListReady}
        isLoading={isLoading}
        phases={phases}
        programId={programId}
      />
    </>
  );
}

export default ProgramPhases;
